import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"
import { useLocation } from "react-router-dom"

import { cn } from "@/lib/utils"

/**
 * Phase 299.b — Hybrid Solid-Pill Tab Theme System
 * =================================================
 *
 * We upgrade the default Shadcn Tabs from the "ghost / muted" active state
 * to a "Solid Pill" active state that mirrors the platform's primary
 * action-button language.
 *
 *   • teal     — default for academy admin, parents, students, settings, finance.
 *   • lime     — Coach Dashboard + on-pitch screens (sun-readable, energetic).
 *   • fuchsia  — Super Admin (high-stakes territory needs the visual reminder).
 *
 * Theme auto-detection: TabsList sniffs the current pathname (via
 * react-router's useLocation) so callers DON'T need to pass `theme` —
 * /coach/* gets lime, /super-admin/* gets fuchsia, everything else stays
 * teal. Explicit `theme="..."` always wins.
 *
 * IMPORTANT: Tailwind's JIT scans source for FULL classname literals. We
 * keep every classname as a complete string (no interpolation) so the
 * compiler picks up every variant.
 */

// Full class literals per theme — keep them complete so Tailwind JIT picks them up.
const THEME_CLASSES = {
  teal: {
    trigger:
      "data-[state=active]:bg-teal-600 data-[state=active]:text-white data-[state=active]:shadow-md " +
      "hover:bg-slate-100 hover:text-teal-700",
  },
  lime: {
    trigger:
      "data-[state=active]:bg-lime-500 data-[state=active]:text-white data-[state=active]:shadow-md " +
      "hover:bg-slate-100 hover:text-lime-700",
  },
  fuchsia: {
    trigger:
      "data-[state=active]:bg-fuchsia-600 data-[state=active]:text-white data-[state=active]:shadow-md " +
      "hover:bg-slate-100 hover:text-fuchsia-700",
  },
};

function _autoTheme(pathname) {
  if (!pathname) return "teal";
  if (pathname.startsWith("/super-admin")) return "fuchsia";
  if (pathname.startsWith("/coach")) return "lime";
  return "teal";
}

// React context lets TabsTrigger pick up the theme set by its parent TabsList
// WITHOUT every caller needing to thread the prop through manually.
const TabsThemeContext = React.createContext("teal");

const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef(({ className, theme, ...props }, ref) => {
  // Hooks must run unconditionally — useLocation safely returns the current
  // route inside any <BrowserRouter>.
  let routeTheme = "teal";
  try {
    const loc = useLocation();
    routeTheme = _autoTheme(loc?.pathname);
  } catch {
    // Outside of a Router (storybook / isolated tests) — fall through.
  }
  const resolvedTheme = theme || routeTheme;

  return (
    <TabsThemeContext.Provider value={resolvedTheme}>
      <TabsPrimitive.List
        ref={ref}
        data-tabs-theme={resolvedTheme}
        className={cn(
          // Container: subtle gray rail. The active pill provides the contrast.
          "inline-flex items-center justify-center gap-1 rounded-lg bg-slate-100 p-1 text-muted-foreground",
          className
        )}
        {...props}
      />
    </TabsThemeContext.Provider>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => {
  const theme = React.useContext(TabsThemeContext) || "teal";
  const tone = THEME_CLASSES[theme] || THEME_CLASSES.teal;

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(
        // Pill base — generous padding, rounded, smooth transitions.
        "inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium",
        "ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "disabled:pointer-events-none disabled:opacity-50",
        // Inactive baseline — let the theme's hover color give the cue.
        "text-slate-600 bg-transparent",
        // Theme-specific active + hover state.
        tone.trigger,
        className
      )}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
